/**
 * The payloads `crm26/realtime/broadcast.py` fans out to `user.{pk}`.
 *
 * Every one of them is deliberately thin — an id and a status, never a message
 * body or a recording URL. The socket says *that* something changed and the
 * client refetches it through DRF, where the permission checks and the
 * serializers live. Pushing the content would duplicate both, and duplicated
 * authorisation is how a real-time layer ends up leaking what the API refuses.
 */

export type ConversationUpdated = {
  type: "conversation.updated";
  conversation: number;
  status: number;
  agent: number | null;
  unread_count: number;
  window_expires_at: string | null;
  last_message_at: string | null;
};

export type WhatsAppMessage = {
  type: "whatsapp.message";
  conversation: number;
  message: number;
  direction: number;
  status: string;
};

export type TelephonyCall = {
  type: "telephony.call";
  call: string;
  status: string;
  duration: number | null;
};

/**
 * The mailbox changed: new mail, a send that landed, or a read state that came
 * back from Zoho. Thinner than the rest on purpose — which mailbox, never which
 * message, and never a subject or an address (D32).
 */
export type MailEvent = {
  type: "mail.message" | "mail.read" | "mail.mailbox";
  mailbox: number;
};

export type Pong = { type: "pong" };

export type ServerEvent =
  ConversationUpdated | WhatsAppMessage | TelephonyCall | MailEvent | Pong;

/**
 * Anything arriving over the socket is untrusted input at a system boundary,
 * even though it came from our own server: a frame could be truncated, or from
 * an older deploy that predates a field. Narrow it before anyone reads it.
 */
export function parseEvent(raw: string): ServerEvent | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null) return null;
  const type = (value as { type?: unknown }).type;
  if (typeof type !== "string") return null;
  switch (type) {
    case "conversation.updated":
    case "whatsapp.message":
    case "telephony.call":
    case "mail.message":
    case "mail.read":
    case "mail.mailbox":
    case "pong":
      return value as ServerEvent;
    default:
      // A newer server sending an event this bundle predates. Not an error —
      // the deploy is simply ahead of the tab that has been open all morning.
      return null;
  }
}
